import {hpcAttrs} from '@github-ui/hpc'
import {BellIcon, SearchIcon, XCircleFillIcon, LogoGithubIcon, MarkGithubIcon} from '@primer/octicons-react'
import {Link, TextInput} from '@primer/react'
import useColorModes from '@github-ui/react-core/use-color-modes'
import React, {type FormEvent, type KeyboardEventHandler, useState, useEffect, useMemo} from 'react'
import {showPreferences} from '@github-ui/cookie-consent'
import PrefixesDialog from '@github-ui/blackbird-dialogs/PrefixesDialog'
import {useNavigateToQuery} from '../hooks/use-navigate-to-query'
import type {HomePayload} from '../types/blackbird-types'
import styles from './Home.module.css'

export default function Home({payload}: {payload: HomePayload}) {
  const [value, setValue] = useState('')
  const returnFocusRef = React.useRef(null)
  const [dialogIsOpen, setDialogIsOpen] = useState(false)
  const navigateToQuery = useNavigateToQuery()

  useHideHeader()

  const handleKeyPress: KeyboardEventHandler = e => {
    // TODO: somehow exempt react apps from this?
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- manual shortcut logic is idiomatic in React
    if (value && e.key === 'Enter') {
      navigateToQuery(value)
    }
  }

  const colorMode = useColorModes()

  const lightIllustrationPath = '/images/modules/search/home-desktop-light.png'
  const lightIllustrationPath2x = '/images/modules/search/home-desktop-light2x.png'
  const lightIllustrationPathWebP = '/images/modules/search/home-desktop-light.webp'
  const lightIllustrationPathWebP2x = '/images/modules/search/home-desktop-light2x.webp'

  const darkIllustrationPath = '/images/modules/search/home-desktop-dark.png'
  const darkIllustrationPath2x = '/images/modules/search/home-desktop-dark2x.png'
  const darkIllustrationPathWebP = '/images/modules/search/home-desktop-dark.webp'
  const darkIllustrationPathWebP2x = '/images/modules/search/home-desktop-dark2x.webp'

  const lightIllustrationPathMobile = '/images/modules/search/home-mobile-light.png'
  const lightIllustrationPathMobile2x = '/images/modules/search/home-mobile-light2x.png'
  const lightIllustrationPathMobileWebP = '/images/modules/search/home-mobile-light.webp'
  const lightIllustrationPathMobileWebP2x = '/images/modules/search/home-mobile-light2x.webp'

  const darkIllustrationPathMobile = '/images/modules/search/home-mobile-dark.png'
  const darkIllustrationPathMobile2x = '/images/modules/search/home-mobile-dark2x.png'
  const darkIllustrationPathMobileWebP = '/images/modules/search/home-mobile-dark.webp'
  const darkIllustrationPathMobileWebP2x = '/images/modules/search/home-mobile-dark2x.webp'

  const colorModeAwareMona = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return darkIllustrationPath
      case 'day':
      default:
        return lightIllustrationPath
    }
  }, [colorMode])

  const colorModeAwareMonaMobile = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return darkIllustrationPathMobile
      case 'day':
      default:
        return lightIllustrationPathMobile
    }
  }, [colorMode])

  const colorModeAwareMonaWebP = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return darkIllustrationPathWebP
      case 'day':
      default:
        return lightIllustrationPathWebP
    }
  }, [colorMode])

  const colorModeAwareMonaMobileWebP = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return darkIllustrationPathMobileWebP
      case 'day':
      default:
        return lightIllustrationPathMobileWebP
    }
  }, [colorMode])

  const colorModeAwareMona2x = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return darkIllustrationPath2x
      case 'day':
      default:
        return lightIllustrationPath2x
    }
  }, [colorMode])

  const colorModeAwareMonaMobile2x = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return darkIllustrationPathMobile2x
      case 'day':
      default:
        return lightIllustrationPathMobile2x
    }
  }, [colorMode])

  const colorModeAwareMonaWebP2x = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return darkIllustrationPathWebP2x
      case 'day':
      default:
        return lightIllustrationPathWebP2x
    }
  }, [colorMode])

  const colorModeAwareMonaWebMobileP2x = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return darkIllustrationPathMobileWebP2x
      case 'day':
      default:
        return lightIllustrationPathMobileWebP2x
    }
  }, [colorMode])

  return (
    <div {...hpcAttrs()} className={styles.homeRoot}>
      <div className={styles.contentArea}>
        <div className={styles.innerContent}>
          <div className={styles.logoWrapper}>
            <div className={styles.desktopLogo}>
              <LogoGithubIcon size={50} />
            </div>
            <div className={styles.mobileLogo}>
              <LogoGithubIcon size={30} />
            </div>
          </div>
          <div className={styles.inputContainer}>
            <TextInput
              aria-label="Search GitHub"
              className={styles.searchInput}
              onKeyPress={handleKeyPress}
              autoFocus
              leadingVisual={() => (
                <div className={styles.leadingVisual}>
                  <SearchIcon className={styles.searchIcon} />
                </div>
              )}
              trailingVisual={() => (
                <div className={styles.trailingVisual}>
                  {value && (
                    <button type="button" onClick={() => setValue('')} className={styles.clearButton}>
                      <XCircleFillIcon className={styles.clearIcon} />
                    </button>
                  )}
                </div>
              )}
              placeholder="Search GitHub"
              value={value}
              onChange={(e: FormEvent<HTMLInputElement>) => setValue(e.currentTarget.value)}
            />
          </div>

          <div className={styles.tipSection}>
            <span className={styles.bellContainer}>
              <BellIcon className={styles.bellIcon} />
              <span className={styles.tipLabel}>Tip</span>
            </span>
            <span className={styles.tipDescription}>
              For an{' '}
              <Link inline href="/search/advanced">
                advanced search
              </Link>
              , use our{' '}
              <Link
                as="button"
                inline
                ref={returnFocusRef}
                onClick={() => {
                  setDialogIsOpen(true)
                }}
              >
                prefixes
              </Link>
            </span>
            <PrefixesDialog
              isOpen={dialogIsOpen}
              returnFocusRef={returnFocusRef}
              rootHelpUrl={payload.helpUrl}
              onDismiss={() => setDialogIsOpen(false)}
            />
          </div>
          <div className={styles.illustrationContainer}>
            <div className={styles.mobileIllustration}>
              <picture>
                <source
                  srcSet={`${colorModeAwareMonaMobileWebP} 1x, ${colorModeAwareMonaWebMobileP2x} 2x`}
                  type="image/webp"
                />
                <img
                  src={colorModeAwareMonaMobile}
                  srcSet={`${colorModeAwareMonaMobile} 1x, ${colorModeAwareMonaMobile2x} 2x`}
                  alt="Illustration of Mona looking through binoculars at the a building a supernatural landscape"
                  width="100%"
                />
              </picture>
            </div>
            <div className={styles.desktopIllustration}>
              <picture>
                <source srcSet={`${colorModeAwareMonaWebP} 1x, ${colorModeAwareMonaWebP2x} 2x`} type="image/webp" />
                <img
                  src={colorModeAwareMona}
                  srcSet={`${colorModeAwareMona} 1x, ${colorModeAwareMona2x} 2x`}
                  alt="Illustration of Mona looking through binoculars at the a building a supernatural landscape"
                  width="100%"
                />
              </picture>
            </div>
          </div>
        </div>
      </div>

      <Footer helpUrl={payload?.helpUrl} cookieConsentEnabled={payload?.cookie_consent_enabled} />
    </div>
  )
}

function Footer({helpUrl, cookieConsentEnabled}: {helpUrl: string; cookieConsentEnabled: boolean | undefined}) {
  const currentYear = new Date().getFullYear()

  return (
    <footer className={styles.footer}>
      <div className={styles.footerLogoSection}>
        <Link aria-label="GitHub" href="/" muted className={styles.footerGithubLink}>
          <MarkGithubIcon className={styles.footerGithubIcon} />
        </Link>

        <span>GitHub, Inc. © {currentYear}</span>
      </div>

      <Link
        href="https://docs.github.com/github/site-policy/github-terms-of-service"
        muted
        className={styles.footerLink}
      >
        Terms
      </Link>

      <Link
        href="https://docs.github.com/site-policy/privacy-policies/github-privacy-statement"
        muted
        className={styles.footerLink}
      >
        Privacy
      </Link>

      <Link href="/security" muted className={styles.footerLink}>
        Security
      </Link>

      <Link href="https://www.githubstatus.com" muted className={styles.footerLink}>
        Status
      </Link>

      <Link href={helpUrl} muted className={styles.footerLink}>
        Docs
      </Link>

      <Link href="https://support.github.com/?tags=dotcom-footer" muted className={styles.footerLink}>
        Contact
      </Link>

      {cookieConsentEnabled ? (
        <>
          <Link as="button" onClick={() => showPreferences()} muted className={styles.footerLink}>
            Manage cookies
          </Link>

          <Link as="button" onClick={() => showPreferences()} muted className={styles.footerLink}>
            Do not share my personal information
          </Link>
        </>
      ) : null}
    </footer>
  )
}

function useHideHeader() {
  useEffect(() => {
    const headerDiv: HTMLElement | null =
      document.querySelector('.AppHeader') ||
      document.querySelector('.Header') ||
      document.querySelector('.HeaderMktg') ||
      document.querySelector('[data-marketing-header]')
    if (!headerDiv) {
      return
    }
    headerDiv.hidden = true
    return () => {
      headerDiv.hidden = false
    }
  }, [])
}
